import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Code2,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Type,
} from "lucide-react";
import {
  EditorContent,
  useEditor,
  type Editor,
  type JSONContent,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { useInput, useTranslate, ValidationError } from "ra-core";

import { cn } from "@/lib/utils";

type SlashRange = {
  from: number;
  to: number;
};

type SlashState = {
  query: string;
  range: SlashRange;
  top: number;
  left: number;
};

type SlashCommand = {
  id: string;
  label: string;
  description: string;
  keywords: string[];
  icon: React.ComponentType<{ className?: string }>;
  run: (range: SlashRange) => void;
};

type NoteRichTextInputProps = {
  source?: string;
  placeholder?: string;
  validate?: any;
  className?: string;
  editorClassName?: string;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const NoteRichTextInput = ({
  source = "text",
  placeholder,
  validate,
  className,
  editorClassName,
  autoFocus,
  onFocus,
  onBlur,
}: NoteRichTextInputProps) => {
  const translate = useTranslate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const latestMarkdownRef = useRef("");
  const [slashState, setSlashState] = useState<SlashState | null>(null);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const { field, fieldState } = useInput({ source, validate });
  const translatedPlaceholder =
    placeholder ?? translate("resources.notes.inputs.add_note");

  const updateSlashState = useCallback((editor: Editor) => {
    const match = getSlashMatch(editor);
    if (!match) {
      setSlashState(null);
      setSelectedCommandIndex(0);
      return;
    }

    const coords = editor.view.coordsAtPos(match.range.from);
    setSlashState({
      ...match,
      top: coords.bottom + 6,
      left: coords.left,
    });
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: translatedPlaceholder,
      }),
    ],
    content: markdownToTiptapDoc(field.value ?? ""),
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": translatedPlaceholder,
        "aria-placeholder": translatedPlaceholder,
        class: cn(
          "min-h-24 w-full max-w-none px-3 py-2 text-sm outline-none",
          "prose prose-sm dark:prose-invert",
          "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
          "[&_h1]:mb-3 [&_h1]:mt-4 [&_h1]:text-2xl [&_h1]:font-semibold",
          "[&_h2]:mb-2 [&_h2]:mt-3 [&_h2]:text-xl [&_h2]:font-semibold",
          "[&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:text-lg [&_h3]:font-semibold",
          "[&_ul]:my-2 [&_ul]:ml-5 [&_ul]:list-disc",
          "[&_ol]:my-2 [&_ol]:ml-5 [&_ol]:list-decimal",
          "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
          "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3",
          "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs",
          "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
          "[&_.is-editor-empty:first-child:before]:pointer-events-none [&_.is-editor-empty:first-child:before]:float-left [&_.is-editor-empty:first-child:before]:h-0 [&_.is-editor-empty:first-child:before]:text-muted-foreground [&_.is-editor-empty:first-child:before]:content-[attr(data-placeholder)]",
          editorClassName,
        ),
      },
      handleKeyDown: (_view, event) => {
        if (!slashState) return false;
        if (filteredCommands.length === 0) return false;

        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedCommandIndex(
            (index) => (index + 1) % filteredCommands.length,
          );
          return true;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedCommandIndex(
            (index) =>
              (index - 1 + filteredCommands.length) % filteredCommands.length,
          );
          return true;
        }

        if (event.key === "Enter") {
          event.preventDefault();
          filteredCommands[selectedCommandIndex]?.run(slashState.range);
          setSlashState(null);
          return true;
        }

        if (event.key === "Escape") {
          event.preventDefault();
          setSlashState(null);
          return true;
        }

        return false;
      },
    },
    onCreate: ({ editor }) => {
      latestMarkdownRef.current = tiptapDocToMarkdown(editor.getJSON());
    },
    onUpdate: ({ editor }) => {
      const markdown = tiptapDocToMarkdown(editor.getJSON());
      latestMarkdownRef.current = markdown;
      field.onChange(markdown);
      updateSlashState(editor);
    },
    onSelectionUpdate: ({ editor }) => updateSlashState(editor),
    onFocus: () => onFocus?.(),
    onBlur: () => {
      setSlashState(null);
      onBlur?.();
    },
    immediatelyRender: false,
  });

  const commands = useMemo<SlashCommand[]>(() => {
    if (!editor) return [];

    const deleteSlash = (range: SlashRange) =>
      editor.chain().focus().deleteRange(range);

    return [
      {
        id: "paragraph",
        label: translate("resources.notes.editor.commands.paragraph", {
          _: "Text",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.paragraph",
          { _: "Plain text paragraph" },
        ),
        keywords: ["text", "paragraph", "текст", "абзац"],
        icon: Type,
        run: (range) => deleteSlash(range).setParagraph().run(),
      },
      {
        id: "heading-1",
        label: translate("resources.notes.editor.commands.heading_1", {
          _: "Heading 1",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.heading_1",
          { _: "Large section heading" },
        ),
        keywords: ["h1", "heading", "заголовок"],
        icon: Heading1,
        run: (range) =>
          deleteSlash(range).setHeading({ level: 1 }).run(),
      },
      {
        id: "heading-2",
        label: translate("resources.notes.editor.commands.heading_2", {
          _: "Heading 2",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.heading_2",
          { _: "Medium section heading" },
        ),
        keywords: ["h2", "heading", "заголовок"],
        icon: Heading2,
        run: (range) =>
          deleteSlash(range).setHeading({ level: 2 }).run(),
      },
      {
        id: "bullet-list",
        label: translate("resources.notes.editor.commands.bullet_list", {
          _: "Bullet list",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.bullet_list",
          { _: "Create a simple list" },
        ),
        keywords: ["list", "ul", "список", "маркер"],
        icon: List,
        run: (range) => deleteSlash(range).toggleBulletList().run(),
      },
      {
        id: "ordered-list",
        label: translate("resources.notes.editor.commands.ordered_list", {
          _: "Numbered list",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.ordered_list",
          { _: "Create a numbered list" },
        ),
        keywords: ["list", "ol", "number", "список", "номер"],
        icon: ListOrdered,
        run: (range) => deleteSlash(range).toggleOrderedList().run(),
      },
      {
        id: "blockquote",
        label: translate("resources.notes.editor.commands.quote", {
          _: "Quote",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.quote",
          { _: "Highlight an important fragment" },
        ),
        keywords: ["quote", "blockquote", "цитата"],
        icon: Quote,
        run: (range) => deleteSlash(range).toggleBlockquote().run(),
      },
      {
        id: "code-block",
        label: translate("resources.notes.editor.commands.code", {
          _: "Code block",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.code",
          { _: "Monospace block" },
        ),
        keywords: ["code", "код"],
        icon: Code2,
        run: (range) => deleteSlash(range).toggleCodeBlock().run(),
      },
      {
        id: "horizontal-rule",
        label: translate("resources.notes.editor.commands.divider", {
          _: "Divider",
        }),
        description: translate(
          "resources.notes.editor.command_descriptions.divider",
          { _: "Separate sections" },
        ),
        keywords: ["divider", "line", "разделитель", "линия"],
        icon: Minus,
        run: (range) => deleteSlash(range).setHorizontalRule().run(),
      },
    ];
  }, [editor, translate]);

  const filteredCommands = useMemo(() => {
    if (!slashState?.query) return commands;

    const query = slashState.query.toLocaleLowerCase();
    return commands.filter((command) =>
      [command.label, command.description, ...command.keywords].some((text) =>
        text.toLocaleLowerCase().includes(query),
      ),
    );
  }, [commands, slashState?.query]);

  useEffect(() => {
    setSelectedCommandIndex(0);
  }, [slashState?.query]);

  useEffect(() => {
    if (!editor || !autoFocus) return;
    requestAnimationFrame(() => editor.commands.focus("end"));
  }, [autoFocus, editor]);

  useEffect(() => {
    if (!editor) return;
    const nextMarkdown = field.value ?? "";
    if (nextMarkdown === latestMarkdownRef.current) return;

    const nextDoc = markdownToTiptapDoc(nextMarkdown);
    editor.commands.setContent(nextDoc, { emitUpdate: false });
    latestMarkdownRef.current = nextMarkdown;
  }, [field.value, editor]);

  useEffect(() => {
    field.ref(wrapperRef.current);
  }, [field]);

  return (
    <div className={cn("relative", className)} ref={wrapperRef}>
      <div
        className={cn(
          "rounded-md border bg-background shadow-xs transition-colors",
          "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
          fieldState.error &&
            "border-destructive focus-within:ring-destructive/20",
        )}
      >
        <EditorContent editor={editor} />
      </div>
      {fieldState.error && (
        <p className="mt-1 text-sm text-destructive">
          <ValidationError error={fieldState.error.message ?? ""} />
        </p>
      )}
      {slashState && filteredCommands.length > 0 && (
        <div
          className="fixed z-50 w-72 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
          style={{ top: slashState.top, left: slashState.left }}
          onMouseDown={(event) => event.preventDefault()}
        >
          <div className="border-b px-3 py-2 text-xs text-muted-foreground">
            {translate("resources.notes.editor.slash_hint", { _: "Blocks" })}
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {filteredCommands.map((command, index) => {
              const Icon = command.icon;
              return (
                <button
                  type="button"
                  key={command.id}
                  className={cn(
                    "flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm",
                    index === selectedCommandIndex &&
                      "bg-accent text-accent-foreground",
                  )}
                  onMouseEnter={() => setSelectedCommandIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    command.run(slashState.range);
                    setSlashState(null);
                  }}
                >
                  <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded border bg-background">
                    <Icon className="size-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium">{command.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {command.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getSlashMatch = (
  editor: Editor,
): { query: string; range: SlashRange } | null => {
  const { state } = editor;
  const { selection } = state;
  if (!selection.empty) return null;

  const $from = selection.$from;
  const textBeforeCursor = $from.parent.textBetween(
    0,
    $from.parentOffset,
    undefined,
    "\ufffc",
  );
  const slashIndex = textBeforeCursor.lastIndexOf("/");
  if (slashIndex === -1) return null;

  const prefix = textBeforeCursor.slice(0, slashIndex);
  if (prefix && !/\s$/.test(prefix)) return null;

  const query = textBeforeCursor.slice(slashIndex + 1);
  if (query.includes("\n")) return null;

  return {
    query,
    range: {
      from: $from.start() + slashIndex,
      to: $from.pos,
    },
  };
};

const markdownToTiptapDoc = (markdown: string): JSONContent => {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const content: JSONContent[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (line.trim() === "---") {
      content.push({ type: "horizontalRule" });
      index += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }
      content.push({
        type: "codeBlock",
        content: [{ type: "text", text: codeLines.join("\n") }],
      });
      index += 1;
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      content.push({
        type: "heading",
        attrs: { level: heading[1].length },
        content: parseInlineText(heading[2]),
      });
      index += 1;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: JSONContent[] = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index])) {
        items.push(createListItem(lines[index].replace(/^[-*]\s+/, "")));
        index += 1;
      }
      content.push({ type: "bulletList", content: items });
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: JSONContent[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index])) {
        items.push(createListItem(lines[index].replace(/^\d+\.\s+/, "")));
        index += 1;
      }
      content.push({ type: "orderedList", attrs: { start: 1 }, content: items });
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quoteLines: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quoteLines.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }
      content.push({
        type: "blockquote",
        content: [
          {
            type: "paragraph",
            content: textLinesToInlineContent(quoteLines),
          },
        ],
      });
      continue;
    }

    const paragraphLines: string[] = [line];
    index += 1;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^(#{1,3})\s+/.test(lines[index]) &&
      !/^[-*]\s+/.test(lines[index]) &&
      !/^\d+\.\s+/.test(lines[index]) &&
      !/^>\s?/.test(lines[index]) &&
      !lines[index].startsWith("```") &&
      lines[index].trim() !== "---"
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }
    content.push({
      type: "paragraph",
      content: textLinesToInlineContent(paragraphLines),
    });
  }

  return {
    type: "doc",
    content: content.length ? content : [{ type: "paragraph" }],
  };
};

const createListItem = (text: string): JSONContent => ({
  type: "listItem",
  content: [
    {
      type: "paragraph",
      content: parseInlineText(text),
    },
  ],
});

const textLinesToInlineContent = (lines: string[]): JSONContent[] =>
  lines.flatMap((line, index) => {
    const content = parseInlineText(line);
    if (index === lines.length - 1) return content;
    return [...content, { type: "hardBreak" }];
  });

const parseInlineText = (text: string): JSONContent[] =>
  text ? [{ type: "text", text }] : [];

const tiptapDocToMarkdown = (doc: JSONContent): string =>
  serializeNodes(doc.content ?? [])
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const serializeNodes = (nodes: JSONContent[]): string[] =>
  nodes
    .map((node) => serializeNode(node))
    .filter((value) => value.trim().length > 0);

const serializeNode = (node: JSONContent): string => {
  switch (node.type) {
    case "paragraph":
      return serializeInline(node.content ?? []);
    case "heading":
      return `${"#".repeat(node.attrs?.level ?? 1)} ${serializeInline(
        node.content ?? [],
      )}`.trim();
    case "bulletList":
      return serializeList(node.content ?? [], "-");
    case "orderedList":
      return serializeOrderedList(node.content ?? [], node.attrs?.start ?? 1);
    case "listItem":
      return serializeNodes(node.content ?? []).join("\n");
    case "blockquote":
      return serializeNodes(node.content ?? [])
        .join("\n")
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
    case "codeBlock":
      return `\`\`\`\n${serializePlainText(node.content ?? [])}\n\`\`\``;
    case "horizontalRule":
      return "---";
    default:
      return serializeInline(node.content ?? []);
  }
};

const serializeList = (items: JSONContent[], marker: string): string =>
  items
    .map((item) =>
      serializeNode(item)
        .split("\n")
        .map((line, index) => `${index === 0 ? `${marker} ` : "  "}${line}`)
        .join("\n"),
    )
    .join("\n");

const serializeOrderedList = (items: JSONContent[], start: number): string =>
  items
    .map((item, index) =>
      serializeNode(item)
        .split("\n")
        .map((line, lineIndex) =>
          lineIndex === 0 ? `${start + index}. ${line}` : `   ${line}`,
        )
        .join("\n"),
    )
    .join("\n");

const serializeInline = (nodes: JSONContent[]): string =>
  nodes.map((node) => serializeInlineNode(node)).join("");

const serializeInlineNode = (node: JSONContent): string => {
  if (node.type === "hardBreak") return "\n";
  if (node.type !== "text") return serializeInline(node.content ?? []);

  return applyMarks(node.text ?? "", node.marks ?? []);
};

const serializePlainText = (nodes: JSONContent[]): string =>
  nodes
    .map((node) => {
      if (node.type === "text") return node.text ?? "";
      if (node.type === "hardBreak") return "\n";
      return serializePlainText(node.content ?? []);
    })
    .join("");

const applyMarks = (
  text: string,
  marks: NonNullable<JSONContent["marks"]>,
): string =>
  marks.reduce((value, mark) => {
    switch (mark.type) {
      case "bold":
        return `**${value}**`;
      case "italic":
        return `_${value}_`;
      case "strike":
        return `~~${value}~~`;
      case "code":
        return `\`${value}\``;
      default:
        return value;
    }
  }, text);
